import React, { useEffect, useState } from "react";
import { Select, Spin, message, Tag, Alert } from "antd";
import { useAppDispatch } from "../../../../../store/hooks";
import { asyncGetAdverseActionPreview } from "../../../../../store/easyApp/easyapp-async";

interface PDFData {
  user_id: string;
  applicant_name: string;
  pdf: string;
  error: string | null;
}

interface PreviewStepProps {
  form: any;
  application: any;
}

const Preview: React.FC<PreviewStepProps> = ({ form, application }) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedApplicant, setSelectedApplicant] = useState<string | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [pdfDataList, setPdfDataList] = useState<PDFData[]>([]);
  const [error, setError] = useState<string | null>(null);

  const dispatch = useAppDispatch();

  const fetchAdverseActionPDFs = async () => {
    const formValues = form.getFieldsValue(true);

    if (!formValues.selectedApplicants?.length) {
      setError("No applicants selected");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result: any = await dispatch(
        asyncGetAdverseActionPreview(formValues)
      ).unwrap();

      if (Array.isArray(result) && result.length > 0) {
        const hasErrors = result.some((pdf) => pdf.error);
        if (hasErrors) {
          message.warning(
            "Some letters generated from template due to missing data"
          );
        }

        setPdfDataList(result);
        setSelectedApplicant(result[0].user_id);
        setError(null);
      } else {
        setError("No PDFs were generated. Please check applicant data.");
        setPdfDataList([]);
      }
    } catch (err) {
      const errorMsg =
        err instanceof Error
          ? err.message
          : "Failed to generate adverse action previews. Please try again.";
      setError(errorMsg);
      message.error(errorMsg);
      setPdfDataList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdverseActionPDFs();
  }, []);

  useEffect(() => {
    let objectUrl: string | null = null;

    if (selectedApplicant && pdfDataList.length > 0) {
      const pdfData = pdfDataList.find((p) => p.user_id === selectedApplicant);

      if (pdfData) {
        try {
          const binaryString = atob(pdfData.pdf);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          const blob = new Blob([bytes], { type: "application/pdf" });
          objectUrl = URL.createObjectURL(blob);
          setPdfUrl(objectUrl);
        } catch {
          setError("Failed to decode PDF data");
        }
      }
    }

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [selectedApplicant, pdfDataList]);

  const applicantOptions = pdfDataList.map((pdf) => ({
    label: (
      <div
        style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}
      >
        <span>{pdf.applicant_name}</span>
        {pdf.error && (
          <Tag color="orange" style={{ marginLeft: "auto" }}>
            Template Only
          </Tag>
        )}
      </div>
    ),
    value: pdf.user_id,
  }));

  const selectedPdfData = pdfDataList.find(
    (p) => p.user_id === selectedApplicant
  );

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 600,
        }}
      >
        <Spin size="large" tip="Generating adverse action letters..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "24px 0" }}>
        <Alert
          message="Failed to Generate Previews"
          description={error}
          type="error"
          showIcon
        />
      </div>
    );
  }

  if (pdfDataList.length === 0) {
    return (
      <div style={{ padding: "24px 0" }}>
        <Alert
          message="No applicants selected"
          description="Please go back to the previous step and select at least one applicant."
          type="info"
          showIcon
        />
      </div>
    );
  }

  return (
    <div style={{ padding: "24px 0" }}>
      <div style={{ marginBottom: 24 }}>
        <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
          Select Applicant to Preview
        </label>
        <Select
          value={selectedApplicant}
          onChange={setSelectedApplicant}
          options={applicantOptions}
          style={{ width: "100%" }}
        />
      </div>

      {selectedPdfData?.error && (
        <Alert
          message="Preview Generated from Template"
          description={`Unable to populate full details: ${selectedPdfData.error}. Showing template with basic information.`}
          type="warning"
          style={{ marginBottom: 24 }}
          showIcon
        />
      )}

      <div
        style={{ height: 600, border: "1px solid #d9d9d9", borderRadius: 4 }}
      >
        {pdfUrl ? (
          <iframe
            src={pdfUrl}
            width="100%"
            height="100%"
            title={`Adverse Action Letter - ${selectedPdfData?.applicant_name}`}
            style={{ border: "none" }}
          />
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Spin />
          </div>
        )}
      </div>
    </div>
  );
};

export default Preview;